import React from 'react';
import { 
  Container, Paper, Typography, Box, Button, CircularProgress, 
  LinearProgress, IconButton, Tooltip 
} from '@mui/material';
import { 
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as ServicesIcon,
  HealthAndSafety as SafetyIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import { usePostOpHub } from './hooks/usePostOpHub';
import StatusRow from './components/StatusRow';

const PostOperativeHub = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    statuses,
    isLoading,
    refetch,
    completionCount,
    totalCount,
    progressPercentage
  } = usePostOpHub(uhid_no);

  const formItems = [
    {
      key: 'postOperativeOrders',
      icon: AssignmentIcon,
      title: 'Post Operative Orders',
      description: 'Post-surgery medical instructions',
      url: `/postOperativeOrders/${uhid_no}`,
      status: statuses?.postOperativeOrders
    },
    {
      key: 'postOperativeInstructions',
      icon: DescriptionIcon,
      title: 'Post Operative Instructions', 
      description: 'Patient care guidelines',
      url: `/postOperativeInstructions/${uhid_no}`,
      status: statuses?.postOperativeInstructions
    },
    {
      key: 'postAnaesthesiaRecovery',
      icon: HospitalIcon,
      title: 'Post Anaesthesia Recovery Score',
      description: 'Recovery monitoring assessment',
      url: `/AldreteScore/${uhid_no}`,
      status: statuses?.postAnaesthesiaRecovery
    },
    {
      key: 'analgesiaAssessment',
      icon: ServicesIcon,
      title: 'Analgesia Assessment Chart',
      description: 'Pain management tracking',
      url: `/postOperativeAssesment/${uhid_no}`,
      status: statuses?.analgesiaAssessment
    },
    {
      key: 'postOperativeHandover',
      icon: SafetyIcon,
      title: 'Post Operative Handover',
      description: 'Post-surgery evaluation',
      url: `/postOperativeHandover/${uhid_no}`,
      status: statuses?.postOperativeHandover
    }
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography color="textSecondary">Checking form statuses...</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={4} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
          {/* Header Section */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #008080 0%, #004d40 100%)', 
            color: 'white', 
            px: 3, py: 4, 
            textAlign: 'center',
            position: 'relative'
          }}>
            <IconButton 
              onClick={() => navigate('/home')} 
              sx={{ position: 'absolute', top: 16, left: 16, color: 'white' }}
            >
              <HomeIcon />
            </IconButton>
            
            <HealingIcon sx={{ fontSize: 60, mb: 1, opacity: 0.9 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Post Operative</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>
              UHID: {uhid_no}
            </Typography>
          </Box>

          <Box sx={{ px: 3, pt: 3, pb: 4 }}>
            {/* Progress Bar */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">Overall Completion</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#008080' }}>
                  {completionCount} / {totalCount} Forms
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5, 
                  backgroundColor: '#e0f2f1',
                  '& .MuiLinearProgress-bar': { backgroundColor: '#008080' }
                }} 
              />
            </Box>

            {/* Form List */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {formItems.map((item) => (
                <StatusRow key={item.key} item={item} navigate={navigate} />
              ))}
            </Box>

            {/* Legend Section */}
            <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: '12px', backgroundColor: '#fafafa', borderStyle: 'dashed' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: 'textSecondary' }}>
                STATUS LEGEND
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                  <Typography variant="caption" color="textSecondary">Completed & Saved</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EditIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                  <Typography variant="caption" color="textSecondary">In Progress (Draft Saved)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UncheckedIcon sx={{ fontSize: 16, color: '#bdbdbd' }} />
                  <Typography variant="caption" color="textSecondary">Not Started Yet</Typography>
                </Box>
              </Box>
            </Paper>

            {/* Action Footer */}
            <Box sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                sx={{ 
                  borderRadius: '10px', 
                  py: 1.5, 
                  borderColor: '#008080', 
                  color: '#008080',
                  '&:hover': { borderColor: '#004d40', backgroundColor: '#e0f2f1' }
                }}
              >
                Refresh Status
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PostOperativeHub;
